import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck, Users as UsersIcon, BadgeCheck, FileText,
  ChevronRight, MailCheck, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const AdminCard = ({ to, icon: Icon, title, subtitle, badge, accent, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group flex items-center justify-between gap-4 p-5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/8 hover:border-white/15 transition"
  >
    <div className="flex items-center gap-4 min-w-0">
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
          accent === "yellow"
            ? "bg-[#E2FF31]/15 text-[#E2FF31]"
            : accent === "pink"
            ? "bg-[#FF2E00]/15 text-[#FF2E00]"
            : "bg-white/10 text-white"
        }`}
      >
        <Icon size={22} />
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-base">{title}</p>
        <p className="text-xs text-white/55 mt-0.5 truncate">{subtitle}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {badge ? (
        <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-[#FF2E00]/20 text-[#FF2E00] font-bold">
          {badge}
        </span>
      ) : null}
      <ChevronRight size={18} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition" />
    </div>
  </Link>
);

const AdminHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [whoami, setWhoami] = useState(null);
  const [counts, setCounts] = useState({ pending_claims: 0, pending_submissions: 0, total_users: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const w = await api.get("/admin/whoami");
        if (cancelled) return;
        setWhoami(w.data);
        if (!w.data?.is_admin) {
          setLoading(false);
          return;
        }
        const [claims, subs, activity] = await Promise.all([
          api.get("/uploads/admin/artist-claims", { params: { status: "pending" } }).catch(() => null),
          api.get("/artists/submissions", { params: { status: "pending" } }).catch(() => null),
          api.get("/admin/users/activity").catch(() => null),
        ]);
        if (cancelled) return;
        setCounts({
          pending_claims: claims?.data?.counts?.pending || 0,
          pending_submissions: (subs?.data?.results || []).length,
          total_users: activity?.data?.stats?.total || 0,
        });
      } catch {
        if (!cancelled) setWhoami({ is_admin: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading) {
    return (
      <div data-testid="admin-loading" className="px-4 sm:px-8 py-12 max-w-3xl mx-auto">
        <div className="h-8 w-40 bg-white/8 rounded animate-pulse mb-6" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-white/5 rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!user || !whoami?.is_admin) {
    return (
      <div data-testid="admin-denied" className="px-4 sm:px-8 py-12 max-w-md mx-auto text-center">
        <div className="w-16 h-16 rounded-full bg-[#FF2E00]/15 flex items-center justify-center mx-auto mb-5">
          <AlertCircle size={28} className="text-[#FF2E00]" />
        </div>
        <h1 className="font-display text-2xl font-semibold mb-2">Admin access only</h1>
        <p className="text-sm text-white/55 mb-6">
          {user
            ? "Your account isn't on the admin allowlist. If this is wrong, ping the platform owner to add your email to ADMIN_EMAILS."
            : "Sign in with an admin Google account to continue."}
        </p>
        <button
          onClick={() => navigate("/")}
          data-testid="admin-go-home"
          className="px-5 py-2.5 rounded-full bg-white text-black font-semibold hover:scale-[1.02] transition"
        >
          Back to home
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-home" className="px-4 sm:px-8 py-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <ShieldCheck size={22} className="text-[#E2FF31]" />
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#E2FF31] font-bold">Admin Center</p>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-1">
        Hey {whoami?.email?.split("@")[0]}
      </h1>
      <p className="text-sm text-white/55 mb-8">Pick a tool below to manage 1o1sound.</p>

      <div className="flex flex-col gap-3">
        <AdminCard
          to="/admin/users"
          icon={UsersIcon}
          title="Users"
          subtitle={`${counts.total_users.toLocaleString()} total · activity, charts, block / unblock, CSV export, email domain status`}
          testId="admin-card-users"
        />
        <AdminCard
          to="/admin/claims"
          icon={BadgeCheck}
          title="Artist claims"
          subtitle="Approve / reject artists claiming their iTunes profile"
          badge={counts.pending_claims > 0 ? `${counts.pending_claims} pending` : null}
          accent={counts.pending_claims > 0 ? "pink" : undefined}
          testId="admin-card-claims"
        />
        <AdminCard
          to="/admin/submissions"
          icon={FileText}
          title="Artist submissions"
          subtitle="Review community-suggested artists for the Trending row"
          badge={counts.pending_submissions > 0 ? `${counts.pending_submissions} pending` : null}
          accent={counts.pending_submissions > 0 ? "pink" : undefined}
          testId="admin-card-submissions"
        />
        <AdminCard
          to="/admin/users#email-domain"
          icon={MailCheck}
          title="Email sender domain"
          subtitle="Resend DNS records for 1o1soundapp.net + verify"
          accent="yellow"
          testId="admin-card-email-domain"
        />
      </div>

      <div className="mt-8 p-4 rounded-2xl bg-white/[0.03] border border-white/8 text-xs text-white/55 flex items-start gap-3">
        <ShieldCheck size={14} className="text-[#E2FF31] mt-0.5 shrink-0" />
        <div>
          Signed in as <span className="text-white font-medium">{whoami?.email}</span>. This account is on the
          <span className="text-white font-mono mx-1">ADMIN_EMAILS</span>
          allowlist — only emails on the list reach these pages.
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
